import type { TimerSession, TimerSnapshot } from './types';

export const MIN_INTERVAL_MS = 6_000;
export const MAX_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const MIN_DURATION_MS = 6_000;
export const MAX_DURATION_MS = 24 * 60 * 60 * 1000;

export function createSession(intervalMs: number, durationMs: number | null): TimerSession {
  return {
    status: 'idle',
    intervalMs: clampInterval(intervalMs),
    durationMs: durationMs == null ? null : clampDuration(durationMs),
    startedAtMs: null,
    elapsedBeforeStartMs: 0,
    lastIntervalIndex: 0,
    completedAtMs: null
  };
}

export function startSession(session: TimerSession, nowMs = Date.now()): TimerSession {
  return {
    ...session,
    status: 'running',
    startedAtMs: nowMs,
    elapsedBeforeStartMs: 0,
    lastIntervalIndex: 0,
    completedAtMs: null
  };
}

export function pauseSession(session: TimerSession, nowMs = Date.now()): TimerSession {
  if (session.status !== 'running') return session;
  return {
    ...session,
    status: 'paused',
    elapsedBeforeStartMs: getElapsedMs(session, nowMs),
    startedAtMs: null
  };
}

export function resumeSession(session: TimerSession, nowMs = Date.now()): TimerSession {
  if (session.status !== 'paused') return session;
  return {
    ...session,
    status: 'running',
    startedAtMs: nowMs
  };
}

export function resetSession(session: TimerSession): TimerSession {
  return {
    ...session,
    status: 'idle',
    startedAtMs: null,
    elapsedBeforeStartMs: 0,
    lastIntervalIndex: 0,
    completedAtMs: null
  };
}

export function updateSessionSettings(
  session: TimerSession,
  intervalMs: number,
  durationMs: number | null
): TimerSession {
  return {
    ...resetSession(session),
    intervalMs: clampInterval(intervalMs),
    durationMs: durationMs == null ? null : clampDuration(durationMs)
  };
}

export function completeSession(session: TimerSession, nowMs = Date.now()): TimerSession {
  return {
    ...session,
    status: 'complete',
    startedAtMs: null,
    elapsedBeforeStartMs: session.durationMs ?? getElapsedMs(session, nowMs),
    completedAtMs: nowMs
  };
}

export function markIntervalPlayed(session: TimerSession, intervalIndex: number): TimerSession {
  return {
    ...session,
    lastIntervalIndex: Math.max(session.lastIntervalIndex, intervalIndex)
  };
}

export function getElapsedMs(session: TimerSession, nowMs = Date.now()): number {
  if (session.status !== 'running' || session.startedAtMs == null) {
    return session.elapsedBeforeStartMs;
  }
  return Math.max(0, session.elapsedBeforeStartMs + nowMs - session.startedAtMs);
}

export function getSnapshot(session: TimerSession, nowMs = Date.now()): TimerSnapshot {
  const elapsedMs = getElapsedMs(session, nowMs);
  const intervalIndex = Math.floor(elapsedMs / session.intervalMs);
  const intervalRemainder = elapsedMs % session.intervalMs;
  const intervalRemainingMs =
    intervalRemainder === 0 && elapsedMs > 0 ? 0 : session.intervalMs - intervalRemainder;
  const sessionRemainingMs =
    session.durationMs == null ? null : Math.max(0, session.durationMs - elapsedMs);
  const durationForProgress = session.durationMs ?? session.intervalMs;
  const progressRatio =
    durationForProgress > 0 ? Math.min(1, Math.max(0, elapsedMs / durationForProgress)) : 0;

  return {
    elapsedMs,
    intervalRemainingMs,
    sessionRemainingMs,
    progressRatio,
    intervalIndex,
    isComplete: session.durationMs != null && elapsedMs >= session.durationMs
  };
}

export function shouldPlayInterval(session: TimerSession, snapshot: TimerSnapshot): boolean {
  if (session.status !== 'running') return false;
  if (snapshot.elapsedMs < session.intervalMs) return false;
  if (snapshot.isComplete) return false;
  return snapshot.intervalIndex > session.lastIntervalIndex;
}

export function clampInterval(ms: number): number {
  if (!Number.isFinite(ms)) return 5 * 60 * 1000;
  return Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, Math.round(ms)));
}

export function clampDuration(ms: number): number {
  if (!Number.isFinite(ms)) return 20 * 60 * 1000;
  return Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, Math.round(ms)));
}

export function minutesToMs(minutes: number): number {
  return Math.round(minutes * 60 * 1000);
}

export function formatTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
